import { Sun, Moon, Cloud, CloudRain, CloudSnow, CloudLightning, CloudFog, BatteryCharging, Home, UtilityPole, PlugZap, Clock, Euro, X, Cpu } from "lucide-react";
import { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { EvDialog } from "./ClienteEv";
import { TigoLayout } from "../tigo/TigoLayout";
import { pw, kwh, n } from "./clienteApi";
import { ClienteMeteo } from "./ClienteMeteo";
import { SlotDialogs } from "./SlotDialogs";
import tigoLogo from "@/assets/tigo-wordmark.png";

// fase del cielo: giorno, alba/tramonto, notte
export const fase = (notte) => {
  const h = new Date().getHours();
  if (notte) return "notte";
  return h < 9 || h >= 17 ? "tramonto" : "giorno";
};
const SKY = {
  giorno: "linear-gradient(180deg,#3BA7F0 0%,#7CCBF7 45%,#CFEFFC 100%)",
  tramonto: "linear-gradient(180deg,#3B4F8F 0%,#E9886A 55%,#FBD3A1 100%)",
  notte: "linear-gradient(180deg,#0B1433 0%,#1B2A5C 55%,#33467F 100%)",
};
const meteoIcon = (c) => (c == null ? null : c >= 95 ? CloudLightning : c >= 71 && c <= 86 ? CloudSnow : c >= 51 ? CloudRain : c >= 45 ? CloudFog : c >= 2 ? Cloud : null);
const CSS = `@keyframes cvCloud{from{transform:translateX(-30%)}to{transform:translateX(130%)}}@keyframes cvRain{from{transform:translateY(-20px)}to{transform:translateY(420px)}}
@keyframes cvTw{0%,100%{opacity:.25}50%{opacity:1}}@keyframes cvDot{0%{left:var(--x1);top:var(--y1);opacity:0}12%,88%{opacity:1}100%{left:var(--x2);top:var(--y2);opacity:0}}@keyframes cvPulse{0%,100%{box-shadow:0 0 0 0 rgba(52,211,153,.45)}50%{box-shadow:0 0 0 14px rgba(52,211,153,0)}}`;

function Weather({ f, m }) {
  const c = m?.codice, rain = c >= 51 && c < 95, cloudy = (m?.nuvole || 0) > 35 || c >= 2;
  return (
    <div className="pointer-events-none absolute inset-0 overflow-hidden" aria-hidden>
      {f === "notte" && [...Array(28)].map((_, i) => (
        <span key={i} className="absolute h-[2px] w-[2px] rounded-full bg-white" style={{ left: `${(i * 37) % 100}%`, top: `${(i * 23) % 45}%`, animation: `cvTw ${2 + (i % 4)}s ${i * 0.2}s infinite` }} />
      ))}
      {cloudy && [0, 1, 2].map((i) => (
        <span key={i} className="absolute h-10 w-32 rounded-full bg-white/60 blur-md" style={{ top: `${8 + i * 9}%`, animation: `cvCloud ${40 + i * 15}s ${-i * 12}s linear infinite` }} />
      ))}
      {rain && [...Array(40)].map((_, i) => (
        <span key={i} className="absolute h-4 w-px bg-white/50" style={{ left: `${(i * 29) % 100}%`, top: 0, animation: `cvRain ${0.8 + (i % 5) * 0.15}s ${i * 0.07}s linear infinite` }} />
      ))}
    </div>
  );
}

const Bubble = ({ x, y, icon: I, color, v, l, testid, onClick, big }) => {
  const cls = `absolute flex ${big ? "w-[121px]" : "w-[84px]"} -translate-x-1/2 -translate-y-1/2 flex-col items-center rounded-2xl bg-white/90 ${big ? "px-2.5 py-2.5" : "px-1.5 py-1.5"} text-center shadow-lg ring-1 ring-white/60 backdrop-blur-md`;
  const inner = (
    <>
      <span className={`flex ${big ? "h-9 w-9" : "h-6 w-6"} items-center justify-center rounded-full`} style={{ background: `${color}22` }}><I className={big ? "h-5 w-5" : "h-3.5 w-3.5"} style={{ color }} /></span>
      <b className={`mt-0.5 ${big ? "text-[19px]" : "text-[13px]"} font-extrabold leading-none text-slate-800`}>{v}</b>
      <span className={`mt-0.5 ${big ? "text-[14px]" : "text-[9.5px]"} font-bold uppercase tracking-wide text-slate-500`}>{l}</span>
    </>
  );
  return onClick
    ? <button type="button" onClick={onClick} className={`${cls} transition-transform hover:scale-105 active:scale-95`} style={{ left: `${x}%`, top: `${y}%` }} data-testid={testid}>{inner}</button>
    : <div className={cls} style={{ left: `${x}%`, top: `${y}%` }} data-testid={testid}>{inner}</div>;
};

// Bolla batteria: cliccabile, mostra livello, potenza di carica/scarica live e salute (SoH)
const BattBubble = ({ x, y, soc, bw, soh, onClick, big }) => {
  const carica = bw < -20, scarica = bw > 20;
  const stato = carica ? "in carica" : scarica ? "in scarica" : "a riposo";
  return (
    <button type="button" onClick={onClick} data-testid="flow-value-batt"
      className={`absolute flex ${big ? "w-[115px]" : "w-[100px]"} -translate-x-1/2 -translate-y-1/2 flex-col items-center rounded-2xl bg-white/90 ${big ? "px-2 py-2" : "px-1.5 py-1.5"} text-center shadow-lg ring-1 ring-white/60 backdrop-blur-md transition-transform hover:scale-105 active:scale-95`}
      style={{ left: `${x}%`, top: `${y}%` }}>
      <span className={`flex ${big ? "h-7 w-7" : "h-6 w-6"} items-center justify-center rounded-full`} style={{ background: "#10B98122" }}><BatteryCharging className={big ? "h-4 w-4" : "h-3.5 w-3.5"} style={{ color: "#10B981" }} /></span>
      <b className={`mt-0.5 ${big ? "text-[15px]" : "text-[13px]"} font-extrabold leading-none text-slate-800`}>{soc ?? "—"}%</b>
      <span className={`mt-0.5 ${big ? "text-[10px]" : "text-[9px]"} font-bold uppercase leading-tight tracking-wide text-slate-500`} data-testid="flow-value-batt-potenza">{(carica || scarica) ? `${stato} · ${pw(bw)}` : stato}</span>
      {soh != null && <span className={`mt-0.5 ${big ? "text-[10px]" : "text-[9px]"} font-semibold text-emerald-600`} data-testid="flow-value-batt-salute">salute {n(soh, 0)}%</span>}
    </button>
  );
};

// croce dei dati: solare in alto (cerchio grande), batteria a sinistra, rete a destra, casa in basso; i flussi passano dal centro
const C = [50, 44], P = { sole: [50, 0], batt: [14, 44], rete: [86, 44], casa: [50, 86], ev: [11, 72] };
const seg = (a, b) => `M${a[0]} ${a[1]} L${b[0]} ${b[1]}`;

// linea animata in coordinate percentuali sopra l'illustrazione
// linea tenue + pallini luminosi che viaggiano nel verso dell'energia (più fitti con più potenza)
const Line = ({ a, b, color, w }) => (
  <path d={seg(a, b)} fill="none" stroke={color} strokeOpacity="0.4" strokeWidth={2 + 6 * w} strokeLinecap="round" vectorEffect="non-scaling-stroke" />
);
const Dots = ({ a, b, color, w, id }) => {
  const n = 2 + Math.round(3 * w), dur = 2.4 - w, size = 6 + 6 * w;
  return [...Array(n)].map((_, i) => (
    <span key={i} className="pointer-events-none absolute -translate-x-1/2 -translate-y-1/2 rounded-full" data-testid={i === 0 ? `flusso-pallini-${id}` : undefined}
      style={{ width: size, height: size, background: color, boxShadow: `0 0 8px 2px ${color}`, "--x1": `${a[0]}%`, "--y1": `${a[1]}%`, "--x2": `${b[0]}%`, "--y2": `${b[1]}%`,
        animation: `cvDot ${dur}s linear ${(-i * dur) / n}s infinite` }} />
  ));
};

// metadati + spiegazioni delle funzioni EV, per stato effettivo
export const EV_FUNZ = {
  dinamica: { icon: Sun, label: "Dinamica", color: "#F5B70A", desc: {
    attiva: "Sta inseguendo il sole: carica di più quando l'impianto produce surplus.",
    in_pausa: "Il sole è impostato, ma ora comanda un'altra funzione con priorità più alta.",
    attesa: "In attesa del sole: ora non c'è abbastanza surplus per caricare." } },
  pun: { icon: Euro, label: "PUN", color: "#16A34A", desc: {
    attiva: "Sta caricando: il prezzo dell'energia all'ingrosso è sotto la media del giorno.",
    in_pausa: "Impostata, ma ora comanda la ricarica programmata o la carica dinamica.",
    attesa: "In attesa delle ore più convenienti: ora il prezzo è sopra la media del giorno." } },
  programmata: { icon: Clock, label: "Programmata", color: "#6366F1", desc: {
    attiva: "Sta caricando secondo gli orari che hai impostato.",
    in_pausa: "Impostata, ma ora comanda un'altra funzione.",
    attesa: "In attesa dell'orario impostato." } },
};
export const EV_STATO_TXT = { attiva: "attiva adesso", in_pausa: "in pausa", attesa: "in attesa" };

// pillole funzioni EV mostrate sotto il nodo EV: evidenziano QUALE funzione comanda davvero adesso
// (attiva, piena di colore + pallino) e quali cedono la precedenza (in pausa / in attesa, attenuate).
// Al tap ognuna mostra una breve spiegazione coerente con il suo stato.
const EvBadges = ({ x, y, items, big }) => {
  const [open, setOpen] = useState(null);
  if (!items.length) return null;
  const sel = items.find((i) => i.key === open);
  const selMeta = sel ? EV_FUNZ[sel.key] : null;
  return (
    <>
      <div className="absolute z-20 flex w-max max-w-[230px] -translate-x-1/2 flex-col items-center gap-1" data-testid="ev-funzioni-attive"
        style={{ left: `${x}%`, top: `calc(${y}% + ${big ? 54 : 44}px)` }}>
        {items.map((it) => {
          const meta = EV_FUNZ[it.key]; if (!meta) return null;
          const live = it.stato === "attiva";
          const col = live ? meta.color : "#94A3B8";
          return (
            <button key={it.key} type="button" onClick={() => setOpen(open === it.key ? null : it.key)} data-testid={`ev-funzione-${it.key}`} data-stato={it.stato}
              className={`inline-flex items-center gap-1 rounded-full bg-white/90 ${big ? "px-2 py-0.5 text-[10px]" : "px-1.5 py-[2px] text-[8.5px]"} font-bold uppercase tracking-wide shadow ring-1 ring-white/60 backdrop-blur-md transition-transform hover:scale-105 active:scale-95 ${live ? "" : "opacity-70"}`}
              style={{ color: col, ...(open === it.key ? { boxShadow: `0 0 0 2px ${col}55` } : {}) }} aria-expanded={open === it.key}>
              {live && <span className="h-1.5 w-1.5 rounded-full" style={{ background: col, boxShadow: `0 0 6px ${col}` }} />}
              <meta.icon className={big ? "h-3 w-3" : "h-2.5 w-2.5"} />{meta.label}
              {!live && <span className="ml-0.5 font-semibold normal-case tracking-normal text-slate-400">· {EV_STATO_TXT[it.stato]}</span>}
            </button>
          );
        })}
      </div>
      {sel && selMeta && createPortal(
        <div className="fixed inset-0 z-[70] flex items-end justify-center bg-black/40 p-4 sm:items-center" onClick={() => setOpen(null)} data-testid={`ev-funzione-${sel.key}-popup`}>
          <div className="cliente-font w-full max-w-sm rounded-3xl bg-white p-5 shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-2.5">
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full" style={{ background: `${selMeta.color}1A`, color: selMeta.color }}>
                <selMeta.icon className="h-5 w-5" />
              </span>
              <div className="min-w-0">
                <div className="text-base font-extrabold text-slate-900">{selMeta.label}</div>
                <div className="text-xs font-bold uppercase tracking-wide" style={{ color: sel.stato === "attiva" ? selMeta.color : "#94A3B8" }}>{EV_STATO_TXT[sel.stato]}</div>
              </div>
              <button type="button" onClick={() => setOpen(null)} className="ml-auto shrink-0 rounded-full p-1.5 text-slate-400 transition-colors hover:text-slate-700" data-testid="ev-funzione-popup-close">
                <X className="h-5 w-5" />
              </button>
            </div>
            <p className="mt-3 text-sm leading-relaxed text-slate-600">{selMeta.desc[sel.stato]}</p>
          </div>
        </div>, document.body)}
    </>
  );
};

export function SkyScene({ imp, big, src, hideOggi, fill }) {
  const f = fase(imp.notte), fl = imp.flussi || {};
  const sole = fl.solare_w ?? imp.potenza_w ?? 0, kwp = (imp.kwp || 1) * 1000;
  const cons = imp.consumi_disponibili && fl.casa_w != null, batt = imp.ha_batteria;
  const bw = fl.batteria_w || 0, gw = fl.rete_w || 0;
  const I = meteoIcon(imp.meteo?.codice) || (f === "notte" ? Moon : Sun);
  const key = `${imp.tipo === "azienda" ? "azienda" : "casa"}-${f === "notte" ? "notte" : "giorno"}`;
  const on = { sole: sole > 20, batt: batt && Math.abs(bw) > 20, rete: cons && Math.abs(gw) > 20, casa: cons && (fl.casa_w || 0) > 20 };
  const img = `/cliente-${key}.webp?v=3`;
  const pct = Math.min(1, sole / kwp);
  // spessore 0-1 in proporzione alla potenza dell'impianto (radice: anche i flussi piccoli restano visibili)
  const sp0 = (x) => Math.sqrt(Math.min(1, Math.abs(x || 0) / kwp));
  const [ev, setEv] = useState(null);
  const [evOpen, setEvOpen] = useState(false);
  useEffect(() => { if (src?.ev) src.ev().then(setEv).catch(() => setEv(null)); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const [tigo, setTigo] = useState(null);
  const [tigoOpen, setTigoOpen] = useState(false);
  useEffect(() => { if (src?.tigo) src.tigo().then(setTigo).catch(() => setTigo(null)); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const tigoN = tigo && tigo.disponibile ? tigo.ottimizzatori_totali : 0;
  const evSt = (ev && ev.disponibile && ev.stazioni) || [];
  const evW = evSt.reduce((a, s) => a + (s.potenza_w || 0), 0);
  const evOn = evSt.some((s) => s.in_carica && (s.potenza_w || 0) > 20);
  const evSole = evSt.some((s) => s.da_sole);
  const evNome = evSt.length === 1 && evSt[0].nome ? `STAZIONE EV ${evSt[0].nome}` : "STAZIONE EV";
  const evOrdine = { attiva: 3, attesa: 2, in_pausa: 1 };
  const evStatoMap = {};
  evSt.forEach((s) => (s.funzioni || []).forEach((f) => {
    if (!evStatoMap[f.key] || evOrdine[f.stato] > evOrdine[evStatoMap[f.key]]) evStatoMap[f.key] = f.stato;
  }));
  const evBadges = ["dinamica", "pun", "programmata"]
    .filter((k) => evStatoMap[k])
    .map((k) => ({ key: k, stato: evStatoMap[k] }))
    .sort((a, b) => evOrdine[b.stato] - evOrdine[a.stato]);
  const F = (id, a, b, color, rev, x) => on[id] && { id, a: rev ? b : a, b: rev ? a : b, color, w: sp0(x) };
  const flows = [F("sole", P.sole, C, "#FACC15", false, sole), F("batt", P.batt, C, "#34D399", bw < 0, bw),
    F("rete", P.rete, C, "#E2E8F0", gw < 0, gw), F("casa", C, P.casa, "#60A5FA", false, fl.casa_w),
    evOn && { id: "ev", a: C, b: P.ev, color: evSole ? "#FACC15" : "#60A5FA", w: sp0(evW) }].filter(Boolean);
  const dark = f !== "giorno";
  const [meteoOpen, setMeteoOpen] = useState(false);
  const [slot, setSlot] = useState(null);
  const prev = imp.meteo?.previsioni?.length > 0;
  return (
    <section className={`overflow-hidden rounded-[2rem] shadow-xl ${big ? "flex flex-col justify-between" : ""} ${fill ? "absolute inset-0" : "relative"}`} style={{ background: SKY[f] }} data-testid="cliente-scene" data-fase={f}>
      <style>{CSS}</style>
      <Weather f={f} m={imp.meteo} />
      <div className={`relative flex items-start justify-between px-5 pt-5 ${dark ? "text-white" : "text-slate-800"}`}>
        {hideOggi ? <div /> : (
          <div>
            <span className={`${big ? "text-[14px]" : "text-xs"} font-semibold opacity-80`}>Prodotta oggi</span>
            <b className={`block ${big ? "text-[35px]" : "text-3xl"} font-extrabold leading-none`} data-testid="cliente-oggi">{kwh(imp.oggi_kwh)}</b>
          </div>
        )}
        <button type="button" disabled={!prev} onClick={() => setMeteoOpen(true)} className="rounded-2xl px-2 py-1 text-right transition-colors enabled:hover:bg-white/15 enabled:active:bg-white/25" data-testid="cliente-meteo">
          <span className={`flex items-center justify-end gap-1.5 ${big ? "text-[28px]" : "text-2xl"} font-extrabold`}><I className={big ? "h-8 w-8" : "h-7 w-7"} style={{ color: I === Sun ? "#FACC15" : undefined }} />{imp.meteo?.temperatura != null ? `${n(imp.meteo.temperatura, 0)}°` : ""}</span>
          <span className={`${big ? "text-[13px]" : "text-[11px]"} font-semibold opacity-80`}>{[imp.comune, imp.meteo?.cielo].filter(Boolean).join(" · ")}</span>
          {prev && <span className={`block ${big ? "text-[12px]" : "text-[10px]"} font-bold uppercase tracking-wider opacity-70`}>prossimi giorni ›</span>}
        </button>
        <ClienteMeteo open={meteoOpen} onClose={() => setMeteoOpen(false)} meteo={imp.meteo} comune={imp.comune} />
      </div>
      <div className={`relative z-10 mx-auto mt-2 flex ${big ? "h-[200px] w-[200px]" : "h-40 w-40"} flex-col items-center justify-center rounded-full bg-white/95 shadow-2xl`} style={{ animation: sole > 20 ? "cvPulse 2.6s infinite" : undefined }} data-testid="cliente-potenza-cerchio">
        <svg className="absolute inset-0 -rotate-90" viewBox="0 0 100 100"><circle cx="50" cy="50" r="46" fill="none" stroke="#E2E8F0" strokeWidth="5" />
          <circle cx="50" cy="50" r="46" fill="none" stroke="#34D399" strokeWidth="5" strokeLinecap="round" strokeDasharray={`${pct * 289} 289`} style={{ transition: "stroke-dasharray 1s" }} /></svg>
        <b className={`${big ? "text-5xl" : "text-4xl"} font-extrabold text-slate-800`} data-testid="cliente-potenza">{sole >= 1000 ? n(sole / 1000, 2) : n(sole, 0)}<small className={`ml-0.5 ${big ? "text-base" : "text-sm"}`}>{sole >= 1000 ? "kW" : "W"}</small></b>
        <span className={`${big ? "text-[13px]" : "text-[11px]"} font-semibold text-slate-500`}>{imp.notte ? "è notte, il sole riposa" : "potenza solare adesso"}</span>
        {tigoN > 0 && (
          <button type="button" onClick={() => setTigoOpen(true)} data-testid="cliente-tigo-badge"
            className={`absolute -bottom-3 left-1/2 z-20 inline-flex w-max -translate-x-1/2 items-center gap-2.5 whitespace-nowrap rounded-full bg-white ${big ? "px-5 py-2.5 text-[17px]" : "px-4 py-2 text-[14px]"} font-bold leading-none text-[#2f5a3c] shadow-lg ring-2 ring-[#3DA935] transition-transform hover:scale-105 active:scale-95`}>
            <img src={tigoLogo} alt="Tigo" className={`${big ? "h-4" : "h-3.5"} w-auto shrink-0`} draggable={false} />
            <span className={`${big ? "h-4" : "h-3.5"} w-px shrink-0 bg-[#3DA935]/40`} />
            <span className="shrink-0">{tigoN} ottimizzatori</span>
          </button>
        )}
      </div>
      <div className="relative -mt-4">
        <img src={img} alt={imp.tipo === "azienda" ? "Azienda" : "Casa"} className={`relative mx-auto w-full select-none ${big ? "max-w-3xl" : "max-w-xl"}`} draggable={false} data-testid="cliente-scene-img" />
        <svg className="pointer-events-none absolute inset-0 h-full w-full overflow-visible" viewBox="0 0 100 100" preserveAspectRatio="none" data-testid="cliente-flussi">
          {flows.map((x) => <Line key={x.id} {...x} />)}
        </svg>
        {flows.map((x) => <Dots key={x.id} {...x} />)}
        {Object.values(on).some(Boolean) && <span className="absolute h-3 w-3 -translate-x-1/2 -translate-y-1/2 rounded-full bg-white shadow-[0_0_10px_rgba(255,255,255,.9)]" style={{ left: `${C[0]}%`, top: `${C[1]}%` }} data-testid="cliente-flussi-centro" />}
        {batt && <BattBubble x={P.batt[0]} y={P.batt[1]} soc={fl.soc ?? imp.soc} bw={bw} soh={imp.salute_batteria} onClick={() => src && setSlot("batteria")} big={big} />}
        {cons && <Bubble x={P.rete[0]} y={P.rete[1]} icon={UtilityPole} color="#64748B" v={pw(gw)} l={gw > 20 ? "prelievo" : gw < -20 ? "vendita" : "rete"} testid="flow-value-rete" onClick={src ? () => setSlot("rete") : undefined} big={big} />}
        {cons && <Bubble x={P.casa[0]} y={P.casa[1]} icon={Home} color="#3B82F6" v={pw(fl.casa_w)} l={imp.tipo === "azienda" ? "azienda" : "casa"} testid="flow-value-casa" onClick={src ? () => setSlot("casa") : undefined} big={big} />}
        {ev && ev.disponibile && <Bubble x={P.ev[0]} y={P.ev[1]} icon={PlugZap} color={evSole ? "#F5B70A" : "#0EA5E9"} v={evOn ? pw(evW) : "ferma"} l={evNome} testid="cliente-ev-node" onClick={() => setEvOpen(true)} big={big} />}
        {ev && ev.disponibile && <EvBadges x={P.ev[0]} y={P.ev[1]} items={evBadges} big={big} />}
      </div>
      <p className={`relative ${big ? "mt-4" : "mt-2"} pb-3 text-center text-[11px] ${dark ? "text-white/70" : "text-slate-600"}`} data-testid="energy-flow-time">{fl.ora ? `Aggiornato alle ${fl.ora}` : "In attesa dei dati"}</p>
      <SlotDialogs slot={slot} onClose={() => setSlot(null)} src={src} soh={imp.salute_batteria} temp={imp.temperatura_batteria} />
      {evOpen && ev && createPortal(<EvDialog src={src} data={ev} onClose={() => setEvOpen(false)} />, document.body)}
      {tigoOpen && tigo && createPortal(
        <div className="fixed inset-0 z-[70] flex items-end justify-center bg-black/50 p-3 sm:items-center" onClick={() => setTigoOpen(false)} data-testid="cliente-tigo-popup">
          <div className="cliente-font max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-3xl bg-neutral-950 p-5 text-white shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="mb-3 flex items-center gap-2">
              <span className="flex h-7 w-7 items-center justify-center rounded-lg" style={{ background: "#3DA93522", color: "#3DA935" }}><Cpu className="h-4 w-4" /></span>
              <span className="text-base font-extrabold">Ottimizzatori Tigo</span>
              <span className="rounded-full bg-[#3DA935]/15 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-[#3DA935]">Demo</span>
              <button type="button" onClick={() => setTigoOpen(false)} className="ml-auto rounded-full p-1.5 text-neutral-400 transition-colors hover:text-white" data-testid="cliente-tigo-popup-close"><X className="h-5 w-5" /></button>
            </div>
            <div className="space-y-6">
              {(tigo.sistemi || []).map((s) => (<TigoLayout key={s.id} data={s} />))}
            </div>
          </div>
        </div>, document.body)}
    </section>
  );
}
